/**
 * マルチフィールドPro
 */
import MultiFieldProComponent from '../components/MultiFieldPro.svelte';
import { defaultFieldBlocks } from '../multiFieldPro/defaultFieldBlocks.js';
import { defaultOptions } from '../multiFieldPro/defaultOptions.js';
import { getCurrentContentType } from './getCurrentContentType.js';
import { randomString } from './randomString.js';
import { deepCopy } from './deepCopy.js';
import { mount } from "svelte";

export class MultiFieldPro {
    constructor(options, mtappVars = window.mtappVars) {
        this.mtappVars = mtappVars;
        this.mtappVars.__system.multiFieldPro = this.mtappVars.__system.multiFieldPro || {};

        // 動作対象のページかどうか確認
        if (!['edit-content-type-data', 'edit-entry', 'edit-page', 'edit-category'].includes(mtappVars.screen_id)) {
            return;
        }

        // オプションをマージ
        this.options = { ...defaultOptions, ...options };

        // fieldBlocks をマージ
        this.options.fieldBlocks = { ...defaultFieldBlocks, ...this.options.fieldBlocks };

        // 必須オプションをチェック
        this.checkRequiredOptions(this.options, 'id');
        this.checkRequiredOptions(this.options, 'label');

        // 現在のコンテキストのコンテンツタイプとターゲットのコンテンツフィールドを取得
        if (mtappVars.screen_id === 'edit-content-type-data') {
            this.objectType = 'content_data';
            this.currentContentType = getCurrentContentType();
            if (!this.currentContentType) {
                throw new Error('Content Type is not found.');
            }

            this.currentContentField = this.currentContentType.fields.find((field) => {
                return field.id === this.options.id;
            });
        }

        // ターゲット要素を取得
        this.getTargetElement();

        // 保存されたデータを復元
        this.restoreSavedData();

        // コンポーネントをセット
        this.setComponent();
    }

    currentContentField = null;
    currentContentType = null;
    isRequired = false;
    options = {};
    objectType = '';
    savedData = '';
    data = {};
    targetElement = null;
    targetContainerElement = null;

    addPropsToObjectWithHandle(obj) {
        // オブジェクトが handle プロパティを持ち、id プロパティを持っていないか、または数字で始まっているか、
        // id プロパティの値が false に相当する場合、ランダムな文字列をセットする
        if (Object.prototype.hasOwnProperty.call(obj, 'handle')) {
            if (!Object.prototype.hasOwnProperty.call(obj, 'id') || !obj.id || /^\d/.test(obj.id)) {
                obj.id = randomString();
            }
            if (!Object.prototype.hasOwnProperty.call(obj, 'expanded')) {
                obj.expanded = true;
            }
        }

        // オブジェクトのプロパティをループして、プロパティの値がオブジェクトの場合は再起的に処理を実行
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                    this.addPropsToObjectWithHandle(obj[key]);
                }
                // プロパティの値が配列の場合、配列の各要素に対して処理を実行
                else if (Array.isArray(obj[key])) {
                    obj[key].forEach((element) => {
                        if (typeof element === 'object' && element !== null) {
                            this.addPropsToObjectWithHandle(element);
                        }
                    });
                }
            }
        }
    }

    /**
     * 必須のオプションをチェックする。存在しなければエラーを発生させる。
     *
     * @static
     * @param {object} options オプション設定
     * @param {string} property 存在チェックするプロパティ
     * @throws {Error} 必須のオプションが存在しない場合
     * @returns {void}
     */
    checkRequiredOptions(options, property = '') {
        if (property === '' || typeof options[property] === 'undefined') {
            throw new Error(`options.${property} is required.`);
        }
    }

    /**
     * ターゲット要素と、その親要素を取得する。また、必須フィールドかどうかを判定する。
     */
    getTargetElement() {
        const mtappVars = this.mtappVars;
        mtappVars.screen_id = this.mtappVars.screen_id; // Unresolved variable を回避

        // 記事・ウェブページ・カテゴリ編集画面に対応
        if (['edit-entry', 'edit-page', 'edit-category'].includes(mtappVars.screen_id)) {
            this.targetElement = document.getElementById(this.options.id);
            if (!this.targetElement) {
                throw new Error(`${this.options.id} not found.`);
            }

            this.targetContainerElement = document.getElementById(`${this.options.id}-field`);
            if (!this.targetContainerElement) {
                throw new Error(`${this.options.id}-field not found.`);
            }

            if (this.targetContainerElement.classList.contains('required')) {
                this.isRequired = true;
            }
        }
        // コンテンツデータ
        else if (mtappVars.screen_id === 'edit-content-type-data') {
            // const contentDataFields = mtappVars.contentDataFields;
            if (typeof mtappVars.contentDataFields === 'undefined') {
                throw new Error(`mtappVars.contentDataFields is not found.`);
            }

            const contentFieldContainerId = `contentField${this.options.id}`;
            if (typeof mtappVars.contentDataFields[contentFieldContainerId] === 'undefined') {
                throw new Error(`${contentFieldContainerId} is not found in mtappVars.contentDataFields.`);
            }

            this.targetContainerElement = mtappVars.contentDataFields[contentFieldContainerId];

            this.targetElement = document.querySelector(`[name="content-field-${this.options.id}"]`);
            if (!this.targetElement) {
                throw new Error(`[name="content-field-${this.options.id}"] not found.`);
            }

            this.isRequired = Number(this.currentContentField.options.required) === 1;
            //     mtappVars.contentDataFields['contentField' + fieldId].find('#block_editor_data').appendTo('#edit-content-type-data-form');
            //     mtappVars.contentDataFields['contentField' + fieldId].find('.modal-blockeditor').appendTo('#edit-content-type-data-form');
        }
        this.targetElement.removeAttribute('required');
        this.targetElement.disabled = true;
        this.targetElement.name = '';
    }

    restoreSavedData() {
        this.savedData = this.targetElement.value;
        this.data = this.savedData ? JSON.parse(this.savedData) : { items: [] };
        // multi-column の中の table-vertical のバグによるデータのマイグレーション
        const fieldBlocks = this.options.fieldBlocks;
        this.data.items.forEach((item) => {
            const fieldBlock = fieldBlocks[item.handle];
            // 設定変更などで保存データの handle が現在の fieldBlocks に存在しないことがある。
            // その場合 fieldBlock が undefined になり .type 参照で TypeError になるためスキップする。
            if (!fieldBlock) return;
            if (fieldBlock.type === 'multi-column') {
                const allFieldsInMultiColumn = [];
                fieldBlock.columns.forEach((column) => {
                    column.fields.forEach((fieldHandle) => {
                        allFieldsInMultiColumn.push(fieldHandle);
                    });
                });
                fieldBlock.columns.forEach((column) => {
                    column.fields.forEach((fieldHandle) => {
                        if (fieldBlocks[fieldHandle].type.startsWith('table-vertical') && typeof item.data[fieldHandle] === 'undefined') {
                            item.data[fieldHandle] = {};
                            fieldBlocks[fieldHandle].fields.forEach((tableFieldHandle) => {
                                item.data[fieldHandle][tableFieldHandle] = deepCopy(item.data[tableFieldHandle]);
                                if (!allFieldsInMultiColumn.includes(tableFieldHandle)) {
                                    delete item.data[tableFieldHandle];
                                }
                            });
                        }
                    });
                });
            }
        });
        // 既存データに重複 ID がある場合に備えてユニーク化する（再起的）
        // まず不足 ID を補完
        this.addPropsToObjectWithHandle(this.data);
        // その後、全ツリーで ID 重複を解消
        this.ensureUniqueIdsRecursively();
        this.targetElement.value = JSON.stringify(this.data);
    }

    /**
     * トップレベル items の id をユニーク化する。
     */
    ensureUniqueItemIds() {
        const seen = new Set();
        for (const item of this.data.items) {
            if (!item.id || seen.has(item.id)) {
                item.id = randomString();
            }
            seen.add(item.id);
        }
    }

    /**
     * multipurpose 内の各グループ要素の id をユニーク化する。
     * @param {object} fieldBlocks
     */
    ensureInnerIdsForMultipurpose(fieldBlocks) {
        for (const item of this.data.items) {
            const fieldBlock = fieldBlocks[item.handle];
            if (!fieldBlock || fieldBlock.type !== 'multipurpose') continue;
            if (typeof item.data !== 'object' || item.data === null) continue;

            const innerGroups = Array.isArray(fieldBlock.innerGroups) ? fieldBlock.innerGroups : [];
            for (const group of innerGroups) {
                const arr = item.data[group.handle];
                if (!Array.isArray(arr)) continue;
                const seen = new Set();
                for (const el of arr) {
                    if (typeof el !== 'object' || el === null) continue;
                    if (!el.id || seen.has(el.id)) {
                        el.id = randomString();
                    }
                    seen.add(el.id);
                }
            }
        }
    }

    /**
     * データ全体を再起的に走査し、すべての id をユニーク化する。
     */
    ensureUniqueIdsRecursively() {
        const seen = new Set();
        const visit = (node) => {
            if (Array.isArray(node)) {
                for (const el of node) visit(el);
                return;
            }
            if (node && typeof node === 'object') {
                if (Object.prototype.hasOwnProperty.call(node, 'id')) {
                    // 数字だけのIDは除外（アセットやコンテンツデータのID）
                    const isNumericId = typeof node.id === 'string' && /^\d+$/.test(node.id);
                    // IDが空の場合も除外
                    const isEmpty = !node.id;
                    if (!isNumericId && !isEmpty) {
                        if (seen.has(node.id)) {
                            node.id = randomString();
                        }
                        seen.add(node.id);
                    }
                }
                for (const key in node) {
                    if (Object.prototype.hasOwnProperty.call(node, key)) {
                        visit(node[key]);
                    }
                }
            }
        };
        visit(this.data);
    }

    /**
     * MultiFieldPro コンポーネントをセットする。
     */
    setComponent() {
        const mtappVars = this.mtappVars;

        // 新しい div 要素を作成
        const div = document.createElement('div');
        div.setAttribute('id', `mtapp-mf-${this.options.id}`);

        this.targetContainerElement.parentNode.insertBefore(div, this.targetContainerElement.nextElementSibling);
        this.targetContainerElement.style.position = 'absolute';
        this.targetContainerElement.style.width = '1px';
        this.targetContainerElement.style.height = '1px';
        this.targetContainerElement.style.opacity = 0;
        this.targetContainerElement.style.overflow = 'hidden';
        this.targetContainerElement.style.zIndex = '-1';

        mount(MultiFieldProComponent, {
                    target: div,
                    props: {
                        mtappVars: mtappVars,
                        data: this.data,
                        fieldBlocks: this.options.fieldBlocks,
                        isRequired: this.isRequired,
                        objectType: this.objectType,
                        options: this.options
                    }
                });
    }
}

// function multiField(options) {
//             setClass: function (handle) {
//                 let classname = fieldBlocks[handle] && fieldBlocks[handle].class ? fieldBlocks[handle].class : '';
//                 if (/col-\d+/.test(classname) === false) {
//                     classname += ' col-12';
//                 }
//                 return classname;
//             },
//             setColumnClass: function (column) {
//                 const classes = ['setColumnClass', 'mb-5', 'col'];
//                 if (column.col) {
//                     classes.push('col-' + column.col);
//                 }
//                 if (column.class) {
//                     classes.push(column.class);
//                 }
//                 return classes.join(' ');
//             },
//             setId: function () {
//                 return Array.prototype.slice.call(arguments).join('-');
//             },
//             getType: function (type) {
//                 const textType = base.multiFieldTextHandles;
//                 if (textType.indexOf(type) !== -1) {
//                     return 'text';
//                 }
//             },
//             assetAddButtonLabel: function (label) {
//                 return label + 'を選択';
//             },
//             insert: function (i) {
//                 const base = this;
//                 Vue.set(base, 'insertPoint', i);
//                 $('#mtapp-mf-modal-' + base.id)
//                     .modal()
//                     .on('hidden.bs.modal', function () {
//                         Vue.set(base, 'insertPoint', -1);
//                     });
//             },
//             insertAbove: function () {
//                 const base = this;
//                 Vue.set(base, 'insertPointDirection', 0);
//             },
//             insertBelow: function () {
//                 const base = this;
//                 Vue.set(base, 'insertPointDirection', 1);
//             },
//         },
//         options.methods
//     );
//
//     // 仮IDをフィールドにセット
//     for (let i = 0; i < data.items.length; i++) {
//         if (typeof data.items[i].id === 'undefined') {
//             data.items[i].id = temporaryId();
//         }
//     }
//
//     mtappVars.multiField['field' + fieldId].app = new Vue({
//         el: '#mtapp-mf-' + fieldId,
//         template: '#mf-container-tmpl' + (version === 2 ? '-v2' : ''),
//         data: data,
//         // updated: function () {
//         //   this.$nextTick(function () {
//         //     console.warn('ビュー全体が再レンダリングされた後にのみ実行されるコード');
//         //     console.warn(this);
//         //   })
//         // },
//         mounted: function () {
//             // body に各フィールドのイベントを付与
//             $('body').on('focus.mtapp-mf-field-datepicker', 'input.mtapp-mf-field-datepicker', function () {
//                 if (!$(this).hasClass('hasDatepicker')) {
//                     $(this).datepicker({
//                         dateFormat: 'yy-mm-dd',
//                         dayNamesMin: ['日', '月', '火', '水', '木', '金', '土'],
//                         monthNames: [
//                             '- 01',
//                             '- 02',
//                             '- 03',
//                             '- 04',
//                             '- 05',
//                             '- 06',
//                             '- 07',
//                             '- 08',
//                             '- 09',
//                             '- 10',
//                             '- 11',
//                             '- 12'
//                         ],
//                         showMonthAfterYear: true,
//                         prevText: '&lt;',
//                         nextText: '&gt;',
//                         onSelect: function () {
//                             this.dispatchEvent(mtappVars.eventInput);
//                         }
//                     });
//                 }
//             });
//         },
//         created: function () {
//             this.ts = new Date().getTime();
//         },
//         updated: function () {
//             // nothing to do
//         },
//         computed: {
//             setContentData: function () {
//                 const items = this.items;
//                 const version = this.version;
//                 const data = { items: items, version: version, ts: this.ts };
//                 return toJSONString(data);
//             },
//             disabledAdd: function () {
//                 return this.maxBlocks > 0 && this.maxBlocks <= this.items.length;
//             }
//         },
//         methods: methods,
//         watch: watch
//     });
// }
